import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { usePlan } from '@/hooks/use-plan';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { UpgradeBanner, PlanBadge } from '@/components/shared/UpgradeBanner';
import { toast } from 'sonner';
import axios from 'axios';
import { Sparkles, Utensils, Activity as ActivityIcon, Moon, Flower2, ArrowRight, Sun, Droplets, Camera, Scale, ChevronRight, Lock, Pill, Clock, Loader2, Upload, Video } from 'lucide-react';
import { API } from '@/lib/api';



const moduleConfig = {
  nutrition: { icon: Utensils, bgClass: 'bg-terracotta/10', textClass: 'text-terracotta', borderClass: 'border-terracotta/20', path: '/tracking' },
  hashimoto: { icon: ActivityIcon, bgClass: 'bg-purple-100', textClass: 'text-purple-600', borderClass: 'border-purple-200', path: '/modules/hashimoto' },
  menopause: { icon: Moon, bgClass: 'bg-orange-100', textClass: 'text-orange-600', borderClass: 'border-orange-200', path: '/modules/menopause' },
  cycle: { icon: Flower2, bgClass: 'bg-pink-100', textClass: 'text-pink-600', borderClass: 'border-pink-200', path: '/modules/cycle' },
  glow: { icon: Sparkles, bgClass: 'bg-amber-100', textClass: 'text-amber-600', borderClass: 'border-amber-200', path: '/modules/glow' },
};
const iconMap = { scale: Scale, sparkles: Sparkles, droplets: Droplets, camera: Camera, sun: Sun, moon: Moon, thermometer: Moon, pill: Pill, dumbbell: Sparkles };

export default function DashboardPage() {
  const { language } = useLanguage();
  const { user, getHeaders } = useAuth();
  const { isFree } = usePlan();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const de = language === 'de';

  const [glowScore, setGlowScore] = useState(null);
  const [actions, setActions] = useState([]);
  const [overview, setOverview] = useState(null);
  const [dashSummary, setDashSummary] = useState(null);
  const [medTimers, setMedTimers] = useState([]);
  const [selfieAnalysis, setSelfieAnalysis] = useState(null);
  const [selfiePhoto, setSelfiePhoto] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [myBookings, setMyBookings] = useState([]);

  useEffect(() => {
    const h = getHeaders();
    Promise.all([
      axios.get(`${API}/modules/glow-score`, { headers: h }).catch(() => ({ data: {} })),
      axios.get(`${API}/modules/next-actions`, { headers: h }).catch(() => ({ data: { actions: [] } })),
      axios.get(`${API}/modules/overview`, { headers: h }).catch(() => ({ data: {} })),
      axios.get(`${API}/dashboard/summary`, { headers: h }).catch(() => ({ data: {} })),
      axios.get(`${API}/modules/medication-timers`, { headers: h }).catch(() => ({ data: { timers: [] } })),
      axios.get(`${API}/bookings/my`, { headers: h }).catch(() => ({ data: [] })),
    ]).then(([s, a, o, d, m, b]) => {
      setGlowScore(s.data);
      setActions(a.data.actions || []);
      setOverview(o.data);
      setDashSummary(d.data);
      setMedTimers(m.data.timers || []);
      setMyBookings((b.data || []).filter(bk => new Date(bk.date + 'T23:59:59') >= new Date()));
      if (d.data?.selfie_analysis) setSelfieAnalysis(d.data.selfie_analysis);
    });
  }, []);

  const handleSelfie = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result;
      setSelfiePhoto(base64);
      try {
        const res = await axios.post(`${API}/modules/glow/selfie-analyze`, { photo: base64 }, { headers: getHeaders() });
        setSelfieAnalysis(res.data.analysis);
        toast.success(de ? 'Selfie analysiert!' : 'Selfie analyzed!');
      } catch {
        toast.error(de ? 'Analyse fehlgeschlagen' : 'Analysis failed');
      }
      setAnalyzing(false);
    };
    reader.readAsDataURL(file);
  };

  const greeting = () => {
    const h = new Date().getHours();
    const name = user?.name?.split(' ')[0];
    if (de) return h < 12 ? `Guten Morgen, ${name}` : h < 18 ? `Guten Tag, ${name}` : `Guten Abend, ${name}`;
    return h < 12 ? `Good Morning, ${name}` : h < 18 ? `Good Afternoon, ${name}` : `Good Evening, ${name}`;
  };

  const score = glowScore?.glow_score || 0;
  const bd = glowScore?.breakdown || {};
  const scoreColor = score >= 80 ? 'text-green-600 border-green-300' : score >= 60 ? 'text-amber-600 border-amber-300' : 'text-red-500 border-red-300';

  const modules = [
    { key: 'nutrition', num: '1', label: de ? 'Ernährung' : 'Nutrition', sub: de ? 'Photo-First KI' : 'Photo-First AI' },
    { key: 'hashimoto', num: '2', label: 'Hashimoto', sub: 'TSH + Selen' },
    { key: 'menopause', num: '3', label: de ? 'Menopause' : 'Menopause', sub: de ? 'Heatmap' : 'Heatmap' },
    { key: 'cycle', num: '4', label: de ? 'Zyklus' : 'Cycle', sub: de ? 'Phasen' : 'Phases' },
    { key: 'glow', num: '5', label: 'Glow', sub: de ? 'Fit/Schön' : 'Fit/Beautiful' },
  ];

  return (
    <div className="space-y-6 pb-24 lg:pb-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="animate-fade-in">
        <div className="flex items-center gap-2">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-terracotta font-semibold">BELOSOPHIE</p>
          <PlanBadge />
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-1">{greeting()}</h1>
      </div>

      {/* GLÖW Score Central + Selfie */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* GLÖW Score - Large */}
        <Card className="md:col-span-2 relative overflow-hidden bg-gradient-to-br from-amber-50 via-white to-orange-50 border border-amber-200/50 rounded-2xl p-6 shadow-[0_4px_24px_-8px_rgba(245,158,11,0.15)]" data-testid="glow-score-hero">
          <div className="flex items-center gap-6">
            <div className="relative shrink-0">
              <div className={`w-28 h-28 rounded-full border-[5px] ${scoreColor} flex items-center justify-center bg-white shadow-xl`}>
                <div className="text-center">
                  <span className="font-display text-4xl font-bold">{score}</span>
                  <p className="font-body text-[9px] text-foreground/40 -mt-1">GLOEW</p>
                </div>
              </div>
              <div className="absolute -bottom-1 -right-1 bg-amber-500 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-md">
                <Sparkles size={14} />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-medium">{de ? 'Dein Glow Score' : 'Your Glow Score'}</h2>
              <p className="font-body text-xs text-foreground/50 mt-1">
                {score >= 80 ? (de ? 'Ausgezeichnet! Weiter so.' : 'Excellent!') :
                 score >= 60 ? (de ? 'Gut! Noch Potenzial.' : 'Good! Room to grow.') :
                 de ? 'Potenzial da – los gehts!' : "Potential there – let's go!"}
              </p>
              <div className="grid grid-cols-5 gap-2 mt-4">
                {[
                  { k: 'skin', l: de ? 'Haut' : 'Skin' },
                  { k: 'waist', l: de ? 'Taille' : 'Waist' },
                  { k: 'energy', l: de ? 'Energie' : 'Energy' },
                  { k: 'sleep', l: de ? 'Schlaf' : 'Sleep' },
                  { k: 'training', l: 'Training' },
                ].map(f => (
                  <div key={f.k} className="text-center">
                    <p className="font-body text-[9px] text-foreground/40">{f.l}</p>
                    <Progress value={bd[f.k] || 0} className="h-1 bg-amber-100 [&>div]:bg-amber-400 mt-0.5" />
                    <p className="font-body text-[10px] font-semibold text-amber-700 mt-0.5">{bd[f.k] || 0}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Selfie View */}
        <Card className="bg-white border border-border/60 rounded-2xl p-5 flex flex-col items-center justify-center" data-testid="selfie-card">
          {selfiePhoto ? (
            <div className="relative w-full">
              <img src={selfiePhoto} alt="Selfie" className="w-full h-32 object-cover rounded-xl" />
              <button onClick={() => fileRef.current?.click()} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/80 flex items-center justify-center shadow">
                <Camera size={14} className="text-foreground/60" />
              </button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} className="w-full h-32 rounded-xl border-2 border-dashed border-amber-300/60 bg-amber-50/50 flex flex-col items-center justify-center gap-2 hover:bg-amber-50 transition-colors" data-testid="selfie-upload-btn">
              {analyzing ? <Loader2 size={24} className="text-amber-500 animate-spin" /> : <Camera size={24} className="text-amber-500" />}
              <span className="font-body text-xs text-amber-700">{analyzing ? (de ? 'Analysiere...' : 'Analyzing...') : (de ? 'Selfie-Check' : 'Selfie Check')}</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden" onChange={handleSelfie} />
          {selfieAnalysis && (
            <div className="mt-3 w-full">
              <p className="font-body text-[10px] text-foreground/50 line-clamp-4">{selfieAnalysis.substring(0, 200)}...</p>
            </div>
          )}
          {!selfieAnalysis && !selfiePhoto && (
            <p className="font-body text-[10px] text-foreground/40 mt-2 text-center">{de ? 'Täglich Selfie = Glow-Trend' : 'Daily selfie = Glow trend'}</p>
          )}
        </Card>
      </div>

      {/* Medication Timers */}
      {medTimers.length > 0 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-5 shadow-sm" data-testid="medication-timers">
          <h3 className="font-display text-base font-medium mb-3 flex items-center gap-2">
            <Pill size={16} className="text-purple-500" />
            {de ? 'Medikamenten-Erinnerung' : 'Medication Reminders'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {medTimers.map((t, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 rounded-xl bg-${t.color === 'purple' ? 'purple' : t.color === 'blue' ? 'blue' : t.color === 'pink' ? 'pink' : 'green'}-50/50 border border-${t.color === 'purple' ? 'purple' : t.color === 'blue' ? 'blue' : t.color === 'pink' ? 'pink' : 'green'}-200/30`} data-testid={`med-timer-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shadow-sm shrink-0">
                  <Clock size={16} className="text-purple-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-medium text-foreground/80">{t.medication}</p>
                  <p className="font-body text-[10px] text-foreground/50 truncate">{t.instruction}</p>
                </div>
                <Badge className="bg-white text-foreground/60 border-0 text-[10px] font-body shrink-0">{t.time}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Upcoming Bookings */}
      {myBookings.length > 0 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-5 shadow-sm" data-testid="dashboard-bookings">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-base font-medium flex items-center gap-2">
              <Video size={16} className="text-terracotta" />
              {de ? 'Deine Termine' : 'Your Appointments'}
            </h3>
            <Button onClick={() => nav('/booking')} variant="ghost" size="sm" className="text-terracotta text-xs h-7 px-2" data-testid="dashboard-book-more">
              {de ? '+ Buchen' : '+ Book'}
            </Button>
          </div>
          <div className="space-y-2">
            {myBookings.slice(0, 3).map((b, i) => (
              <button key={i} onClick={() => nav('/booking')} className="w-full flex items-center gap-3 p-3 rounded-xl bg-warmgray/30 hover:bg-warmgray/50 transition-colors text-left" data-testid={`dashboard-booking-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-terracotta/10 flex items-center justify-center shrink-0">
                  <Video size={18} className="text-terracotta" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-medium">{b.date} – {b.time} Uhr</p>
                  <p className="font-body text-[10px] text-foreground/40">15 Min · 1:1 mit Belinda</p>
                </div>
                <Badge className="bg-green-100 text-green-700 border-0 text-[9px] rounded-full shrink-0">{de ? 'Bestätigt' : 'Confirmed'}</Badge>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Next Best Actions */}
      {actions.length > 0 && (
        <Card className="bg-white border border-border/60 rounded-2xl p-5 shadow-sm" data-testid="next-actions-card">
          <h3 className="font-display text-base font-medium mb-3">{de ? 'Deine nächsten Schritte' : 'Your Next Steps'}</h3>
          <div className="space-y-2">
            {actions.slice(0, 4).map((a, i) => {
              const Icon = iconMap[a.icon] || Sun;
              const mc = moduleConfig[a.module] || moduleConfig.nutrition;
              return (
                <button key={i} onClick={() => nav(mc.path)} className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-warmgray/50 transition-all group text-left" data-testid={`action-${i}`}>
                  <div className={`w-8 h-8 rounded-lg ${mc.bgClass} flex items-center justify-center shrink-0`}>
                    <Icon size={16} className={mc.textClass} />
                  </div>
                  <p className="font-body text-sm text-foreground/70 flex-1 group-hover:text-foreground">{a.text}</p>
                  {a.priority === 'high' && <Badge className="bg-terracotta/10 text-terracotta border-0 text-[9px] font-body rounded-full shrink-0">{de ? 'Wichtig' : 'Important'}</Badge>}
                  <ChevronRight size={14} className="text-foreground/20 shrink-0" />
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Upgrade Banner */}
      {isFree && <UpgradeBanner feature={de ? 'KI-Coach + Selfie Glow-Analyse + Heatmap' : 'AI Coach + Selfie Glow Analysis + Heatmap'} />}

      {/* 5 Modules Compact */}
      <div>
        <h3 className="font-display text-base font-medium mb-3">{de ? 'Module' : 'Modules'}</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="modules-grid">
          {modules.map(m => {
            const cfg = moduleConfig[m.key];
            const Icon = cfg.icon;
            const locked = isFree && (m.key === 'glow' || m.key === 'hashimoto');
            const entries = overview?.[m.key]?.entries_this_week || 0;
            return (
              <Card key={m.key} onClick={() => !locked ? nav(cfg.path) : nav('/pricing')}
                className={`relative bg-white border ${cfg.borderClass} rounded-xl p-4 hover:shadow-md transition-all cursor-pointer ${locked ? 'opacity-60' : ''}`}
                data-testid={`module-card-${m.key}`}>
                {locked && <Lock size={12} className="absolute top-2 right-2 text-foreground/30" />}
                <div className={`w-8 h-8 rounded-lg ${cfg.bgClass} flex items-center justify-center mb-2`}>
                  <Icon size={16} className={cfg.textClass} />
                </div>
                <p className="font-display text-sm font-medium">{m.label}</p>
                <p className="font-body text-[10px] text-foreground/40 mt-0.5">{locked ? (de ? 'Basis' : 'Basis') : `${entries}x`}</p>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => nav('/booking')} size="sm" className="rounded-full bg-terracotta text-white px-5 text-sm shadow-md shadow-terracotta/20" data-testid="quick-booking-btn">
          <Video size={12} className="mr-1" /> {de ? 'Call buchen' : 'Book Call'}
        </Button>
        {isFree ? (
          <Button onClick={() => nav('/pricing')} size="sm" className="rounded-full bg-sage text-white px-5 text-sm" data-testid="quick-chat-btn">
            <Lock size={12} className="mr-1" /> {de ? 'KI-Coach' : 'AI Coach'}
          </Button>
        ) : (
          <Button onClick={() => nav('/chat')} size="sm" className="rounded-full bg-sage text-white px-5 text-sm" data-testid="quick-chat-btn">
            {de ? 'KI-Coach' : 'AI Coach'} <ArrowRight size={14} className="ml-1" />
          </Button>
        )}
        <Button onClick={() => nav('/tracking')} size="sm" variant="outline" className="rounded-full px-5 text-sm" data-testid="quick-tracking-btn">
          {de ? 'Tracking' : 'Tracking'}
        </Button>
        <Button onClick={() => nav('/content')} size="sm" variant="outline" className="rounded-full px-5 text-sm" data-testid="quick-content-btn">
          {de ? 'Bibliothek' : 'Library'}
        </Button>
        <Button onClick={() => nav('/progress')} size="sm" variant="outline" className="rounded-full px-5 text-sm" data-testid="quick-progress-btn">
          {de ? 'Fortschritt' : 'Progress'}
        </Button>
      </div>
    </div>
  );
}
